/**
 * 텍스트 학습지 레이아웃 손잡이 (WP-K).
 * 백엔드 services/worksheet_layout_knobs.py 와 같은 규칙 — 상태 없음.
 */

export const FONT_SCALES = Object.freeze(['s', 'm', 'l']);
export const DENSITIES = Object.freeze(['compact', 'normal', 'loose']);
export const FIGURE_SIZES = Object.freeze(['s', 'm', 'l']);
export const DEFAULT_FONT_SCALE = 'm';
export const DEFAULT_DENSITY = 'normal';
export const DEFAULT_FIGURE_SIZE = 'm';

export const FONT_SCALE_FACTOR = Object.freeze({ s: 0.88, m: 1, l: 1.14 });
export const DENSITY_ORDER = Object.freeze(['loose', 'normal', 'compact']);
export const FONT_TIGHTER = Object.freeze({ l: 'm', m: 's' });

export const FIGURE_SLOT_HEIGHT_PX = Object.freeze({ s: 72, m: 120, l: 180 });
export const FIGURE_SLOT_LABEL = '그림';
export const FIGURE_SLOT_BORDER = '#D1D5DB';
export const FIGURE_SLOT_FILL = '#FAFAFA';
export const FIGURE_SLOT_LABEL_COLOR = '#9CA3AF';

const FIT_RESULT_KEYS = new Set(['pages_actual', 'fit_result']);

const asLower = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

export function normalizeFontScale(value, fallback = DEFAULT_FONT_SCALE) {
  const text = asLower(value);
  return FONT_SCALES.includes(text) ? text : fallback;
}

export function normalizeDensity(value, fallback = DEFAULT_DENSITY) {
  const text = asLower(value);
  return DENSITIES.includes(text) ? text : fallback;
}

export function normalizeFigureSlotSize(value, fallback = DEFAULT_FIGURE_SIZE) {
  const text = asLower(value);
  return FIGURE_SIZES.includes(text) ? text : fallback;
}

export function normalizeTargetPages(value) {
  if (value == null || typeof value === 'boolean') return null;
  const number = typeof value === 'number' ? value : (typeof value === 'string' && /^-?\d+$/.test(value.trim())
    ? Number(value.trim())
    : NaN);
  if (!Number.isInteger(number) || number < 1 || number > 30) return null;
  return number;
}

export function figureSlotEnabled(item) {
  if (!item || typeof item !== 'object') return false;
  const slot = item.figure_slot;
  if (slot === true) return true;
  if (slot && typeof slot === 'object') return Boolean(slot.enabled !== false);
  return false;
}

export function figureSlotSizeOf(item, fallback = DEFAULT_FIGURE_SIZE) {
  if (!item || typeof item !== 'object' || !item.figure_slot || typeof item.figure_slot !== 'object') {
    return fallback;
  }
  return normalizeFigureSlotSize(item.figure_slot.size, fallback);
}

export function figureSlotSpec(size = DEFAULT_FIGURE_SIZE) {
  const resolved = normalizeFigureSlotSize(size);
  return {
    size: resolved,
    height: FIGURE_SLOT_HEIGHT_PX[resolved],
    label: FIGURE_SLOT_LABEL,
    border: FIGURE_SLOT_BORDER,
    fill: FIGURE_SLOT_FILL,
    labelColor: FIGURE_SLOT_LABEL_COLOR,
  };
}

export function enabledFigureSlotNumbers(outline) {
  if (!outline || typeof outline !== 'object') return [];
  const questions = Array.isArray(outline.questions) ? outline.questions : [];
  const items = questions.length
    ? questions
    : (Array.isArray(outline.sections) ? outline.sections : []);
  return items
    .filter((item) => figureSlotEnabled(item))
    .map((item) => {
      const raw = item.number ?? item.section_number;
      const number = typeof raw === 'number' ? raw : Number(raw);
      return Number.isInteger(number) ? number : null;
    })
    .filter((number) => number != null);
}

export function planFitAttempts(fontScale = DEFAULT_FONT_SCALE, density = DEFAULT_DENSITY, fontScaleLocked = false) {
  const startFont = normalizeFontScale(fontScale);
  const startDensity = normalizeDensity(density);
  const startIndex = Math.max(0, DENSITY_ORDER.indexOf(startDensity));
  const attempts = [];
  const seen = new Set();
  DENSITY_ORDER.slice(startIndex).forEach((dens) => {
    const key = `${startFont}:${dens}`;
    if (seen.has(key)) return;
    seen.add(key);
    attempts.push({ font_scale: startFont, density: dens });
  });
  if (!fontScaleLocked) {
    const tighter = FONT_TIGHTER[startFont];
    if (tighter) {
      const lastDensity = attempts.length ? attempts[attempts.length - 1].density : 'compact';
      const key = `${tighter}:${lastDensity}`;
      if (!seen.has(key)) {
        attempts.push({ font_scale: tighter, density: lastDensity });
      }
    }
  }
  return attempts;
}

export function chooseLayoutFit(attempts, pageCounts, targetPages) {
  if (!Array.isArray(attempts) || attempts.length === 0) {
    return {
      font_scale: DEFAULT_FONT_SCALE,
      density: DEFAULT_DENSITY,
      pages_actual: null,
      fit_result: 'overflow',
      attempt_index: -1,
    };
  }
  const target = Number(targetPages);
  for (let index = 0; index < attempts.length; index += 1) {
    const pages = pageCounts[index];
    if (Number.isFinite(pages) && Number(pages) <= target) {
      return {
        font_scale: attempts[index].font_scale,
        density: attempts[index].density,
        pages_actual: Number(pages),
        fit_result: 'fitted',
        attempt_index: index,
      };
    }
  }
  const lastIndex = attempts.length - 1;
  const lastPages = pageCounts[lastIndex];
  return {
    font_scale: attempts[lastIndex].font_scale,
    density: attempts[lastIndex].density,
    pages_actual: Number.isFinite(lastPages) ? Number(lastPages) : null,
    fit_result: 'overflow',
    attempt_index: lastIndex,
  };
}

export function overflowMessage(targetPages, actualPages) {
  return `${targetPages}쪽에 맞추기엔 내용이 많아 ${actualPages}쪽으로 만들었어요`;
}

export function layoutHintForSignature(hint) {
  if (!hint || typeof hint !== 'object') return null;
  const next = {};
  Object.keys(hint).sort().forEach((key) => {
    if (FIT_RESULT_KEYS.has(key)) return;
    next[key] = hint[key];
  });
  return Object.keys(next).length ? next : null;
}

export function pickHintForState(hint, outline) {
  const source = hint && typeof hint === 'object' ? hint : {};
  const out = {};
  const columns = source.columns;
  if (columns === 1 || columns === 2 || columns === '1' || columns === '2') {
    out.columns = Number(columns);
  }
  const paper = asLower(source.paper_style);
  if (paper === 'csat' || paper === 'school') out.paper_style = 'csat';
  if (FONT_SCALES.includes(asLower(source.font_scale))) out.font_scale = asLower(source.font_scale);
  if (DENSITIES.includes(asLower(source.density))) out.density = asLower(source.density);
  const target = normalizeTargetPages(source.target_pages);
  if (target != null) out.target_pages = target;
  const slides = Number(source.target_slides);
  if (Number.isInteger(slides) && slides >= 1 && slides <= 40) out.target_slides = slides;
  const actual = Number(source.pages_actual);
  if (Number.isInteger(actual) && actual >= 0) out.pages_actual = actual;
  const fit = asLower(source.fit_result);
  if (fit === 'fitted' || fit === 'overflow') out.fit_result = fit;
  const slots = source.figure_slots;
  if (slots === 'none' || slots === 'all') out.figure_slots = slots;
  else if (Array.isArray(slots)) {
    const numbers = slots
      .map((item) => (typeof item === 'number' ? item : Number(item)))
      .filter((item) => Number.isInteger(item) && item >= 1);
    if (numbers.length) out.figure_slots = numbers;
    else out.figure_slots = 'none';
  }
  if (FIGURE_SIZES.includes(asLower(source.figure_slot_size))) {
    out.figure_slot_size = asLower(source.figure_slot_size);
  }
  if (!out.figure_slots) {
    const numbers = enabledFigureSlotNumbers(outline);
    if (numbers.length) out.figure_slots = numbers;
  }
  return out;
}

export function persistableFitHint(currentHint, builtHint, studentPageCount) {
  const current = currentHint && typeof currentHint === 'object' ? currentHint : {};
  const built = builtHint && typeof builtHint === 'object' ? builtHint : {};
  const next = { ...current };
  let changed = false;
  ['font_scale', 'density', 'target_pages', 'pages_actual', 'fit_result', 'figure_slots', 'figure_slot_size']
    .forEach((key) => {
      if (built[key] === undefined) return;
      if (JSON.stringify(built[key]) === JSON.stringify(current[key])) return;
      next[key] = built[key];
      changed = true;
    });
  const actual = Number.isInteger(studentPageCount) ? studentPageCount : Number(built.pages_actual);
  if (Number.isInteger(actual) && actual > 0 && next.pages_actual !== actual) {
    next.pages_actual = actual;
    changed = true;
  }
  return { hint: next, changed };
}
